import { saveAs } from 'file-saver';
import { cond, equals, first, last, pipe, split, stubTrue } from 'lodash/fp';
import { useCallback } from 'react';
import { CMS_BASE_URL } from '@/utils/constants';
import useDownloadResource from './download/useDownloadResource';
import useErrorParser from './download/useErrorParser';

const addFirstSlash = (value: string) =>
  pipe(
    first,
    cond([
      [equals('/'), () => value],
      [stubTrue, () => `/${value}`],
    ]),
  )(value);
const useDownloadResultContent = (url: string, contentRef?: any, cardDescription?: string) => {
  const {
    downloadResource,
    loading: isDownloading,
    error,
  } = useDownloadResource({
    method: 'GET',
  });
  const getFileExtension = (str: string) => {
    const lastIndex = str.lastIndexOf('.');
    return str.substring(lastIndex + 1);
  };

  const blobStoragePath = addFirstSlash(url);
  const fileName = pipe(split('/'), last)(blobStoragePath) || '';
  const convertFileName = (str: string) => str.replace(/\s+/g, '_').toLowerCase();
  const fileExtension = getFileExtension(fileName);

  const handleCustomEvent = useCallback(() => {
    const positionNode = contentRef?.current?.closest(
      '[data-datalayer-position]',
    ) as HTMLDivElement;
    const position = positionNode?.dataset?.datalayerPosition || '';

    contentRef?.current?.dispatchEvent(
      new CustomEvent('gtm:custom:download-file', {
        bubbles: true,
        detail: {
          target: {
            position,
            linkText: cardDescription,
            fileName: convertFileName(fileName),
            fileExtension,
          },
        },
      }),
    );
  }, [cardDescription, contentRef, fileExtension, fileName]);

  const downloadContent = useCallback(async () => {
    const requestUrl = `${CMS_BASE_URL}/restservices/portal/assets/blobstorage${blobStoragePath}`;
    const blobResource = await downloadResource({ url: requestUrl });
    if (!blobResource) return;

    const blob = new Blob([blobResource], { type: '' });
    handleCustomEvent();
    saveAs(blob, fileName);
  }, [downloadResource, blobStoragePath, fileName, handleCustomEvent]);

  const { parsedErrorMessage } = useErrorParser(error);

  return {
    downloadContent,
    parsedErrorMessage,
    isDownloading,
    error,
    fileName,
  };
};

export default useDownloadResultContent;
